import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { 
  Shield, 
  Gift, 
  TrendingUp, 
  Users, 
  Copy, 
  Check, 
  LogOut, 
  MousePointerClick,
  CreditCard,
  Award,
  Clock,
  ExternalLink,
  ChevronRight,
  Share2,
  Sparkles,
  Target,
  Tag
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MILESTONE_COLORS = {
  5: { bg: "from-emerald-500/20 to-emerald-600/20", border: "border-emerald-500/30", text: "text-emerald-400" },
  10: { bg: "from-blue-500/20 to-blue-600/20", border: "border-blue-500/30", text: "text-blue-400" },
  25: { bg: "from-purple-500/20 to-purple-600/20", border: "border-purple-500/30", text: "text-purple-400" },
  50: { bg: "from-amber-500/20 to-amber-600/20", border: "border-amber-500/30", text: "text-amber-400" },
  100: { bg: "from-red-500/20 to-red-600/20", border: "border-red-500/30", text: "text-red-400" },
};

export default function PartnerDashboard() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("partner_token");
    if (!token) {
      navigate("/partner-login");
      return;
    }
    fetchDashboardData(token);
  }, [navigate]);

  const fetchDashboardData = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/affiliate/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 401) {
        localStorage.removeItem("partner_token");
        localStorage.removeItem("partner_data");
        navigate("/partner-login");
        return;
      }

      if (!response.ok) {
        throw new Error("Failed to fetch dashboard data");
      }

      const data = await response.json();
      setDashboardData(data);
    } catch (error) {
      toast.error("Kon dashboard data niet laden");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyLink = () => {
    const refCode = dashboardData?.affiliate?.referral_code;
    if (refCode) {
      const url = `https://proofix.nl?ref=${refCode}`;
      navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link gekopieerd! Deel hem nu met je netwerk.");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleShareLink = async () => {
    const refCode = dashboardData?.affiliate?.referral_code;
    if (refCode) {
      const url = `https://proofix.nl?ref=${refCode}`;
      // Try native share if available (mobile)
      if (navigator.share) {
        try {
          await navigator.share({
            title: 'Proofix DBA Check',
            text: 'Check of jouw werkrelatie voldoet aan de DBA-regels!',
            url: url,
          });
          toast.success("Bedankt voor het delen!");
        } catch (err) {
          // User cancelled or share failed, fallback to copy
          handleCopyLink();
        }
      } else {
        // Fallback to copy
        handleCopyLink();
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("partner_token");
    localStorage.removeItem("partner_data");
    navigate("/partner-login");
    toast.success("Je bent uitgelogd");
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Dashboard laden...</p>
        </div>
      </div>
    );
  }

  const { affiliate, stats, milestones, next_milestone, rewards, recent_referrals } = dashboardData || {};
  
  // Always use production URL for referral links
  const referral_url = affiliate?.referral_code 
    ? `https://proofix.nl?ref=${affiliate.referral_code}` 
    : null;

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800/50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-white">Proofix</span>
            <span className="text-sm text-slate-500 hidden sm:inline">Partner</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-slate-400 text-sm hidden sm:inline">
              {affiliate?.first_name} {affiliate?.last_name}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-slate-400 hover:text-white"
            >
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Uitloggen</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Top Motivation Banner */}
        {next_milestone && (stats?.paid_referrals || 0) < 5 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-gradient-to-r from-emerald-500/10 via-blue-500/10 to-emerald-500/10 rounded-xl border border-emerald-500/20 p-4"
          >
            <p className="text-center text-white font-medium">
              Je bent <span className="text-emerald-400 font-bold">{next_milestone.remaining} referral{next_milestone.remaining !== 1 ? 's' : ''}</span> verwijderd van je eerste <span className="text-emerald-400 font-bold">€15 beloning</span> 🎯
            </p>
          </motion.div>
        )}

        {/* Action Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative mb-8 overflow-hidden"
        >
          {/* Glow effect background */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 via-purple-500/10 to-emerald-500/20 rounded-2xl blur-xl" />
          
          <div className="relative bg-gradient-to-br from-slate-900/90 to-slate-800/90 rounded-2xl border border-slate-700/50 p-6 sm:p-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              {/* Left: Welcome & Goal */}
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-full mb-4">
                  <Sparkles className="w-4 h-4 text-emerald-400" />
                  <span className="text-sm text-emerald-400 font-medium">Welkom, {affiliate?.first_name}!</span>
                </div>
                
                <h1 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                  Deel je link en verdien je eerste €15 Bol.com cadeaukaart
                </h1>
                
                <p className="text-slate-400 mb-6 max-w-xl">
                  Help ondernemers inzicht te krijgen in hun DBA-situatie en ontvang beloningen voor elke betaalde check.
                </p>

                {/* Action Steps */}
                <div className="bg-slate-950/50 rounded-xl p-4 border border-slate-700/50 mb-6">
                  <p className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                    <Target className="w-4 h-4 text-blue-400" />
                    Start met verdienen in 2 stappen
                  </p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400 font-bold text-sm">1</div>
                      <span className="text-slate-300 text-sm">Deel je referral link</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400 font-bold text-sm">2</div>
                      <span className="text-slate-300 text-sm">Laat mensen de check doen</span>
                    </div>
                  </div>
                </div>

                {/* CTA Button */}
                <Button
                  onClick={handleShareLink}
                  size="lg"
                  className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold px-8 py-6 rounded-xl shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all"
                  data-testid="share-link-cta"
                >
                  <Share2 className="w-5 h-5 mr-2" />
                  Deel mijn link
                </Button>
              </div>

              {/* Right: Next Milestone Highlight */}
              {next_milestone && (
                <div className="lg:w-72 flex-shrink-0">
                  <div className="relative">
                    {/* Glow effect */}
                    <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500/30 to-blue-500/30 rounded-2xl blur-lg animate-pulse" />
                    
                    <div className="relative bg-gradient-to-br from-emerald-500/10 to-blue-500/10 rounded-2xl border border-emerald-500/30 p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <Gift className="w-5 h-5 text-emerald-400" />
                        <span className="text-sm font-medium text-emerald-400">Volgende beloning</span>
                      </div>
                      
                      <p className="text-2xl font-bold text-white mb-2">{next_milestone.reward}</p>
                      
                      <p className="text-sm text-slate-300 mb-4">
                        Nog {next_milestone.remaining} referral{next_milestone.remaining !== 1 ? 's' : ''} tot je eerste €15 beloning 🎯
                      </p>
                      
                      {/* Progress bar */}
                      <div className="h-3 bg-slate-800 rounded-full overflow-hidden">
                        <motion.div 
                          initial={{ width: 0 }}
                          animate={{ width: `${Math.min(100, ((stats?.paid_referrals || 0) / next_milestone.threshold) * 100)}%` }}
                          transition={{ duration: 1, delay: 0.5 }}
                          className="h-full bg-gradient-to-r from-emerald-500 to-blue-500 rounded-full"
                        />
                      </div>
                      <p className="text-xs text-slate-500 mt-2 text-right">
                        {stats?.paid_referrals || 0} / {next_milestone.threshold}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Referral Link Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-slate-900/50 rounded-2xl border border-slate-800 p-6 mb-8"
        >
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h2 className="text-lg font-semibold text-white mb-1">Jouw referral link</h2>
              <p className="text-sm text-slate-400">Deel je link en verdien je eerste beloning</p>
            </div>
            <Button
              onClick={handleShareLink}
              className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold px-5 rounded-xl shadow-lg shadow-blue-500/20"
              data-testid="share-link-cta-secondary"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Deel mijn link
            </Button>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 bg-slate-950/50 rounded-xl px-4 py-3 border border-slate-700/50">
              <code className="text-sm text-blue-400 break-all">
                {referral_url}
              </code>
            </div>
            <Button
              onClick={handleCopyLink}
              className={`px-6 ${copied 
                ? "bg-emerald-500 hover:bg-emerald-600" 
                : "bg-slate-700 hover:bg-slate-600"
              } text-white`}
              data-testid="copy-referral-link"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Gekopieerd!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Kopieer
                </>
              )}
            </Button>
          </div>

          <div className="mt-4 pt-4 border-t border-slate-700/30 flex flex-wrap gap-4 text-xs text-slate-500">
            <span>Referral code: <span className="text-blue-400 font-mono">{affiliate?.referral_code}</span></span>
            <span>Cookie geldig: 30 dagen</span>
          </div>
          
          {/* Partner Discount Info Block */}
          <div className="mt-4 p-4 rounded-xl bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/20">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                <Gift className="w-4 h-4 text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-white font-medium mb-1">25% korting voor jouw referrals</p>
                <p className="text-xs text-slate-400 leading-relaxed">
                  Wanneer iemand jouw link gebruikt, krijgt die persoon toegang tot <span className="text-purple-400 font-semibold">25% korting</span> met 
                  code <span className="font-mono text-blue-400 font-semibold">PROOFIX25</span> na het achterlaten van een e-mailadres.
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-slate-900/50 rounded-xl border border-slate-800 p-5"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-slate-400 text-sm">Clicks</span>
              <MousePointerClick className="w-5 h-5 text-slate-600" />
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-white">{stats?.total_clicks || 0}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.18 }}
            className="bg-slate-900/50 rounded-xl border border-slate-800 p-5"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-slate-400 text-sm">Kortingen</span>
              <Tag className="w-5 h-5 text-purple-500" />
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-purple-400">{stats?.discount_claims || 0}</p>
            <p className="text-xs text-slate-500 mt-2">E-mails verzameld</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-slate-900/50 rounded-xl border border-slate-800 p-5"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-slate-400 text-sm">Referrals</span>
              <Users className="w-5 h-5 text-slate-600" />
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-white">{stats?.total_referrals || 0}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-slate-900/50 rounded-xl border border-slate-800 p-5"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-slate-400 text-sm">Betaald</span>
              <CreditCard className="w-5 h-5 text-slate-600" />
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-emerald-400">{stats?.paid_referrals || 0}</p>
            <p className="text-xs text-slate-500 mt-2">Telt mee voor je beloning</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-slate-900/50 rounded-xl border border-slate-800 p-5"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-slate-400 text-sm">Conversie</span>
              <TrendingUp className="w-5 h-5 text-slate-600" />
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-white">{stats?.conversion_rate || 0}%</p>
          </motion.div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Milestones Progress */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="bg-slate-900/50 rounded-2xl border border-slate-800 p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-white">Alle beloningen</h2>
              <Award className="w-5 h-5 text-slate-600" />
            </div>

            <div className="space-y-3">
              {milestones?.map((milestone, index) => {
                const isEarned = (stats?.paid_referrals || 0) >= milestone.threshold;
                const isNext = next_milestone?.threshold === milestone.threshold;
                const rewardEarned = rewards?.earned?.find(r => r.threshold === milestone.threshold);
                const colors = MILESTONE_COLORS[milestone.threshold] || MILESTONE_COLORS[5];
                const remaining = milestone.threshold - (stats?.paid_referrals || 0);
                
                return (
                  <motion.div 
                    key={milestone.threshold}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 + index * 0.05 }}
                    className={`relative flex items-center justify-between p-4 rounded-xl border transition-all ${
                      isEarned 
                        ? `bg-gradient-to-r ${colors.bg} ${colors.border}` 
                        : isNext
                          ? "bg-gradient-to-r from-blue-500/10 to-emerald-500/10 border-blue-500/30 shadow-lg shadow-blue-500/10"
                          : "bg-slate-800/30 border-slate-700/50"
                    }`}
                  >
                    {/* Next milestone indicator */}
                    {isNext && !isEarned && (
                      <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-2 h-8 bg-gradient-to-b from-blue-500 to-emerald-500 rounded-full" />
                    )}
                    
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold ${
                        isEarned ? colors.text + " bg-white/10" : isNext ? "text-blue-400 bg-blue-500/20" : "text-slate-500 bg-slate-800"
                      }`}>
                        {milestone.threshold}
                      </div>
                      <div>
                        <p className={`font-medium ${isEarned || isNext ? "text-white" : "text-slate-400"}`}>
                          {milestone.reward}
                        </p>
                        <p className="text-xs text-slate-500">
                          {milestone.threshold} betaalde referrals
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      {isEarned ? (
                        rewardEarned?.sent ? (
                          <span className="text-xs text-emerald-400 bg-emerald-500/10 px-3 py-1 rounded-full">
                            Verzonden
                          </span>
                        ) : (
                          <span className="text-xs text-amber-400 bg-amber-500/10 px-3 py-1 rounded-full">
                            In afwachting
                          </span>
                        )
                      ) : isNext ? (
                        <span className="text-xs text-blue-400 font-medium">
                          Nog {remaining} referral{remaining !== 1 ? 's' : ''} tot {milestone.reward} 🎯
                        </span>
                      ) : (
                        <span className="text-xs text-slate-500">
                          Nog {remaining} referral{remaining !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Recent Referrals */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-slate-900/50 rounded-2xl border border-slate-800 p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-white">Recente referrals</h2>
              <Clock className="w-5 h-5 text-slate-600" />
            </div>

            {recent_referrals?.length > 0 ? (
              <div className="space-y-3">
                {recent_referrals.map((referral, index) => (
                  <div 
                    key={referral.id || index}
                    className="flex items-center justify-between p-4 bg-slate-800/30 rounded-xl border border-slate-700/50"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-slate-700/50 rounded-full flex items-center justify-center">
                        <Users className="w-5 h-5 text-slate-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium">
                          {referral.name || "Anoniem"}
                        </p>
                        <p className="text-xs text-slate-500">
                          {referral.created_at 
                            ? new Date(referral.created_at).toLocaleDateString("nl-NL", {
                                day: "numeric",
                                month: "short",
                                year: "numeric"
                              })
                            : referral.timestamp
                              ? new Date(referral.timestamp).toLocaleDateString("nl-NL", {
                                  day: "numeric",
                                  month: "short",
                                  year: "numeric"
                                })
                              : "Onbekend"
                          }
                        </p>
                      </div>
                    </div>
                    <div>
                      {referral.payment_status === "paid" || referral.premium_unlocked ? (
                        <span className="text-xs text-emerald-400 bg-emerald-500/10 px-3 py-1 rounded-full flex items-center gap-1">
                          <Check className="w-3 h-3" />
                          Betaald
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400 bg-slate-700/50 px-3 py-1 rounded-full">
                          In afwachting
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-slate-800/50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-slate-600" />
                </div>
                <p className="text-slate-400 mb-2">Nog geen referrals</p>
                <p className="text-sm text-slate-500">
                  Deel je link om je eerste referral te krijgen!
                </p>
              </div>
            )}
            
            {/* Explanation */}
            <div className="mt-4 pt-4 border-t border-slate-700/30">
              <p className="text-xs text-slate-500 text-center">
                💡 Zodra iemand betaalt, telt deze mee voor je beloning
              </p>
            </div>
          </motion.div>
        </div>

        {/* Rewards Summary */}
        {rewards?.pending?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            className="mt-8 bg-gradient-to-br from-amber-500/10 to-amber-600/10 rounded-2xl border border-amber-500/20 p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <Gift className="w-6 h-6 text-amber-400" />
              <h2 className="text-lg font-semibold text-white">
                Beloningen in afwachting
              </h2>
            </div>
            <p className="text-slate-300 mb-4">
              Je hebt {rewards.pending.length} beloning{rewards.pending.length !== 1 ? 'en' : ''} verdiend! 
              We nemen binnenkort contact met je op.
            </p>
            <div className="flex flex-wrap gap-2">
              {rewards.pending.map((reward) => (
                <span 
                  key={reward.threshold}
                  className="px-4 py-2 bg-amber-500/20 rounded-lg text-amber-300 font-medium"
                >
                  {reward.reward}
                </span>
              ))}
            </div>
          </motion.div>
        )}

        {/* Tips Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-8 bg-slate-900/30 rounded-2xl border border-slate-800 p-6"
        >
          <h2 className="text-lg font-semibold text-white mb-4">Tips voor meer referrals</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-blue-500/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-blue-400 font-bold">1</span>
              </div>
              <div>
                <p className="text-white font-medium">Deel op social media</p>
                <p className="text-sm text-slate-400">LinkedIn en Facebook werken goed voor ZZP'ers.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-purple-500/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-purple-400 font-bold">2</span>
              </div>
              <div>
                <p className="text-white font-medium">Stuur naar collega's</p>
                <p className="text-sm text-slate-400">Ken je andere freelancers? Deel de link!</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-emerald-500/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-emerald-400 font-bold">3</span>
              </div>
              <div>
                <p className="text-white font-medium">Voeg toe aan signature</p>
                <p className="text-sm text-slate-400">Zet je link in je e-mail handtekening.</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
